import { SSO_CLOSE_WATCHDOG_MS, SSO_ERROR_PARAM, SSO_TICKET_PARAM } from '@constants/sso';

import { hasOpener, requestOpenerTakeover } from './ssoPopupBridge';

import { SsoBootstrapParams } from '@services/api/auth/ssoTypes';

/**
 * Reads the Microsoft SSO result the backend appended to the frontend root URL
 * (`/?sso_ticket=...` or `/?sso_error=...`) and strips it from the address bar.
 *
 * Must run BEFORE React Router mounts: `/` is wrapped in PrivateRoute, which
 * redirects to /login the moment it sees an empty token — the ticket would be
 * thrown away unread. Hence the synchronous call from src/index.js ahead of
 * root.render().
 *
 * The URL is cleaned via history.replaceState (not a navigation) so the
 * single-use, 60-second ticket never lingers in the address bar, in history, or
 * in a copy-pasted link. Returns null when neither parameter is present, which
 * is the normal case for every ordinary page load.
 */
export const consumeSsoBootstrapParams = (): SsoBootstrapParams | null => {
  const params = new URLSearchParams(window.location.search);

  const ticket = params.get(SSO_TICKET_PARAM);
  const errorCode = params.get(SSO_ERROR_PARAM);

  if (!ticket && !errorCode) {
    return null;
  }

  params.delete(SSO_TICKET_PARAM);
  params.delete(SSO_ERROR_PARAM);

  const query = params.toString();
  const cleanUrl = `${window.location.pathname}${query ? `?${query}` : ''}${window.location.hash}`;

  window.history.replaceState({}, document.title, cleanUrl);

  return { ticket, errorCode };
};

/**
 * Decides who owns the SSO result on this page load, and is what the entry point
 * awaits before rendering the app.
 *
 * - no SSO parameters in the URL -> `null`, the ordinary page load;
 * - parameters present, no opener -> hand them to the app (same-tab flow);
 * - parameters present with an opener -> offer them to the opener. Acknowledged
 *   means this window is the popup and is closing — the promise settles (to a
 *   plain `null` page load) only if the window survives the close watchdog.
 *   Unacknowledged means nobody was listening, so this window completes the
 *   sign-in itself.
 *
 * `consumeSsoBootstrapParams` runs synchronously on entry (before the first
 * await) so the ticket leaves the address bar immediately.
 */
export const resolveSsoBootstrap = async (): Promise<SsoBootstrapParams | null> => {
  const params = consumeSsoBootstrapParams();

  if (!params || !hasOpener()) {
    return params;
  }

  const openerTookOver = await requestOpenerTakeover(params);

  if (openerTookOver) {
    // window.close() is already in flight, but the browser honours a scripted
    // close only for script-opened windows: a tab that merely HAS an opener
    // (target="_blank" without rel="noopener") survives it. Without a watchdog
    // this window would stay a permanently blank document — render never runs
    // while this promise is pending. If we are still alive after the grace
    // period, resolve to an ordinary page load: the opener owns the ticket, and
    // PrivateRoute routes this window to /login as usual.
    return new Promise<SsoBootstrapParams | null>(resolve => {
      setTimeout(() => resolve(null), SSO_CLOSE_WATCHDOG_MS);
    });
  }

  return params;
};
